import { InfoCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { Empty, Row, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type {
  FilterValue,
  SortOrder,
  SorterResult,
} from 'antd/es/table/interface';
import dayjs from 'dayjs';
import fileDownload from 'js-file-download';
import { useContext, useEffect, useState } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { StatusCell } from 'app/components/lists/cells';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useBookingsCsvDownload } from 'app/hooks/data/core/extranet/bookings/useBookingsCsvDownload';
import { VCC_STATUS } from 'app/typings/core/common/enum/vccStatus';
import type { ExtranetBooking } from 'app/typings/core/extranet/bookings/bookings';
import { parseUppercaseSnakeTextToCapitalize } from 'app/utils/string';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import styles from '../ExtranetBookings.module.scss';
import { formatPax, getPackageFilterGroups, getTooltipText } from '../utils';

import { MODERN_VCC_STATUS_CONFIG } from './BookingDetails/_components/VirtualCard/utils/virtualCard.utils';
import { ExtranetBookingsHeader } from './ExtranetBookingsHeader';
import { PackageFilterDropdown } from './PackageFilterDropdown';

type FilterUpdates = Partial<{
  pageNumber: number;
  search: string;
  beginning: string;
  ending: string;
  sortingColumn: string;
  sortingOrder: 'ASC' | 'DESC';
  status?: string[];
  vccStatus?: string[];
  packageIds?: string[];
}>;

type ExtranetBookingsContentProps = {
  bookings?: ExtranetBooking[];
  hasVccBookings?: boolean;
  isLoading: boolean;
  filters: FilterUpdates;
  updateFilters: (updates: FilterUpdates) => void;
  pagination?: {
    pageNumber: number;
    pageSize: number;
    pageCount: number;
    itemCount: number;
  };
  searchInput: string;
  setSearchInput: (value: string) => void;
  handleDateChange: (startDate?: string, endDate?: string) => void;
  setBookingCode: (code: string) => void;
  setIsLegacyBooking: (isLegacy: boolean) => void;
};

type ExtranetBookingUI = Omit<ExtranetBooking, 'children'> & {
  paxChildren: number;
};

export const ExtranetBookingsContent = ({
  bookings,
  hasVccBookings,
  isLoading,
  filters,
  updateFilters,
  pagination,
  searchInput,
  setSearchInput,
  handleDateChange,
  setBookingCode,
  setIsLegacyBooking,
}: ExtranetBookingsContentProps) => {
  const { hotelId } = useContext(HotelContext);
  const { packsList, oldPacksList } = useContext(ModernHotelContext);
  const { currencyFormatter } = useContext(CurrencyContext);

  const [isDownloadLaunched, setIsDownloadLaunched] = useState(false);

  const { data: csvData, isFetching: isDownloading } = useBookingsCsvDownload({
    hotelId,
    enabled: isDownloadLaunched,
    search: filters.search || undefined,
    sorting:
      filters.sortingColumn && filters.sortingOrder
        ? `${filters.sortingColumn}:${filters.sortingOrder}`
        : undefined,
    beginning: filters.beginning,
    ending: filters.ending,
  });

  useEffect(() => {
    if (csvData) {
      fileDownload(csvData, 'bookings.csv');
      setIsDownloadLaunched(false);
    }
  }, [csvData]);

  // Map backend "children" to a safe UI field ("paxChildren") because Ant Design
  // treats any "children" property as React node children, which breaks the table.
  const mappedBookings: ExtranetBookingUI[] = (bookings ?? []).map(
    ({ children, ...rest }) => ({
      ...rest,
      paxChildren: children,
    })
  );

  const handleRowActions = ({ code, isLegacy }: ExtranetBookingUI) => ({
    onClick: () => {
      setBookingCode(code);
      setIsLegacyBooking(isLegacy);
    },
  });

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    tableFilters: Record<string, FilterValue | null>,
    sorter: SorterResult<ExtranetBookingUI> | SorterResult<ExtranetBookingUI>[]
  ) => {
    const sortConfig = Array.isArray(sorter) ? sorter[0] : sorter;
    const sortingOrder = sortConfig.order
      ? sortConfig.order === 'ascend'
        ? 'ASC'
        : 'DESC'
      : undefined;

    const statusFilter = tableFilters.status as string[] | null;
    const vccStatusFilter = tableFilters.vccStatus as VCC_STATUS[] | null;
    const packageIdsFilter = tableFilters.roomAndPack as string[] | null;

    updateFilters({
      pageNumber: paginationConfig.current,
      sortingColumn: sortConfig.field ? String(sortConfig.field) : undefined,
      sortingOrder,
      status: statusFilter ?? undefined,
      vccStatus: vccStatusFilter ?? undefined,
      packageIds: packageIdsFilter ?? undefined,
    });
  };

  const toAntdSortOrder = (
    order: 'ASC' | 'DESC' | undefined
  ): SortOrder | undefined => {
    if (!order) {
      return undefined;
    }

    return order === 'ASC' ? 'ascend' : 'descend';
  };

  const grayCell = (title: string) => (
    <div className={styles.grayHeader}>{title}</div>
  );

  const packageFilterGroups = getPackageFilterGroups(packsList, oldPacksList);

  const backendToFrontendColumn: Record<string, string> = {
    CREATED_AT: 'createdAt',
    GLOBAL_SCORE: 'globalScore',
    CHECK_IN: 'checkIn',
    CHECK_OUT: 'checkOut',
  };

  const columns: ColumnsType<ExtranetBookingUI> = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortOrder:
        filters.sortingColumn &&
        backendToFrontendColumn[filters.sortingColumn] === 'createdAt'
          ? toAntdSortOrder(filters.sortingOrder)
          : undefined,
      render: (createdAt) => dayjs(createdAt).format('DD/MM/YYYY'),
      width: 94,
    },
    {
      title: 'Guest',
      key: 'customerName',
      render: (_, record) => {
        return (
          <div className={styles.wrapper}>
            <span>
              {record.customerFirstName} {record.customerLastName}
            </span>
            <span className={styles.subTitle}>
              {formatPax({
                adults: record.adults,
                children: record.paxChildren,
                babies: record.babies,
              })}
            </span>
          </div>
        );
      },
      width: 214,
    },
    {
      title: 'Check-in',
      dataIndex: 'checkIn',
      key: 'checkIn',
      sorter: true,
      align: 'center',
      render: (checkIn) => dayjs(checkIn).format('DD/MM/YYYY'),
      sortOrder:
        filters.sortingColumn &&
        backendToFrontendColumn[filters.sortingColumn] === 'checkIn'
          ? toAntdSortOrder(filters.sortingOrder)
          : undefined,
      width: 94,
    },
    {
      title: 'Check-out',
      dataIndex: 'checkOut',
      key: 'checkOut',
      sorter: true,
      align: 'center',
      render: (checkOut) => dayjs(checkOut).format('DD/MM/YYYY'),
      sortOrder:
        filters.sortingColumn &&
        backendToFrontendColumn[filters.sortingColumn] === 'checkOut'
          ? toAntdSortOrder(filters.sortingOrder)
          : undefined,
      width: 94,
    },
    {
      title: 'Nights',
      dataIndex: 'nightsCount',
      key: 'nightsCount',
      render: grayCell,
      width: 64,
    },
    {
      title: 'Package & Room',
      key: 'roomAndPack',
      width: 143,
      filterDropdown: (filterDropdownProps) => (
        <PackageFilterDropdown
          {...filterDropdownProps}
          groups={packageFilterGroups}
        />
      ),
      filteredValue: filters.packageIds ? filters.packageIds : null,
      onFilter: () => true,
      render: (_: unknown, record: ExtranetBookingUI) => (
        <div className={styles.wrapper}>
          <span>{record.nameForHotel}</span>
          {(record.roomName || record.roomCategoryName) && (
            <span className={styles.subTitle}>
              {record.roomName ?? record.roomCategoryName}
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Extras',
      dataIndex: 'extrasCount',
      key: 'extrasCount',
      render: grayCell,
      width: 60,
    },
    {
      title: 'Total incl. VAT',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (totalAmount) => currencyFormatter(totalAmount),
      width: 90,
    },
    ...(hasVccBookings
      ? [
          {
            title: 'VCC Status',
            dataIndex: 'vccStatus',
            key: 'vccStatus',
            filters: Object.values(VCC_STATUS).map((value) => ({
              text: parseUppercaseSnakeTextToCapitalize(value),
              value,
            })),
            filteredValue: filters.vccStatus ?? null,
            onFilter: () => true,
            render: (vccStatus: VCC_STATUS) => {
              const status = MODERN_VCC_STATUS_CONFIG[vccStatus];

              if (!status) {
                return (
                  <Row align="middle">
                    <Tag color="default">Bank transfer</Tag>
                  </Row>
                );
              }

              const tooltipText = getTooltipText(vccStatus);

              return (
                <Row align="middle">
                  <Tag color={status.color}>{status.text}</Tag>
                  {!!tooltipText && vccStatus === VCC_STATUS.TO_REFUND && (
                    <Tooltip title={tooltipText}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  )}
                </Row>
              );
            },
            width: 126,
          },
        ]
      : []),
    {
      title: 'Booking ID',
      dataIndex: 'code',
      key: 'code',
      render: grayCell,
      width: 90,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Confirmed', value: 'ACCEPTED' },
        { text: 'Canceled', value: 'CANCELED' },
      ],
      filteredValue: filters.status ?? null,
      onFilter: () => true,
      render: (status: string) => StatusCell(status.toLowerCase()),
      width: 90,
    },
    {
      render: () => <MoreOutlined />,
      width: 32,
    },
  ];

  return (
    <Table<ExtranetBookingUI>
      title={() => (
        <ExtranetBookingsHeader
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          beginning={filters.beginning}
          ending={filters.ending}
          onDateChange={handleDateChange}
          onDownloadCsv={() => {
            setIsDownloadLaunched(true);
            amplitudeTrack('Download Booking CSV');
          }}
          isDownloading={isDownloading}
          pagination={pagination}
          onPageChange={(page) => updateFilters({ pageNumber: page })}
        />
      )}
      columns={columns}
      dataSource={mappedBookings}
      scroll={{ x: 'max-content' }}
      loading={{
        spinning: isLoading,
      }}
      rowKey={(record) => `${record.isLegacy ? 'legacy' : 'new'}-${record.id}`}
      onChange={handleTableChange}
      onRow={handleRowActions}
      pagination={{
        current: pagination?.pageNumber,
        pageSize: pagination?.pageSize,
        total: pagination?.itemCount,
        showSizeChanger: false,
        simple: true,
        position: ['bottomRight'],
      }}
      sticky
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No booking found"
          />
        ),
      }}
    />
  );
};
